import {AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, Renderer2} from '@angular/core';
import {TimeSpan} from "../models/TimeSpan";
import {DateTime} from "luxon";

export interface TimeInterface {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  timer: any;
}

@Component({
  selector: 'app-duration-timer',
  template: ' <ng-content></ng-content>',
  styles: [`
    :host {
      display: inline-block;
    }
  `],
})
export class DurationTimerComponent implements AfterViewInit, OnDestroy {
  private timeoutId: any;
  private ngContentSchema: string;

  private seconds: number = 0;
  private minutes: number = 0;
  private hours: number = 0;
  private days: number = 0;
  private startMoment: DateTime;

  @Input() startDate: Date;
  @Input() duration: TimeSpan;
  @Input() autoStart: boolean;
  @Input() maxTimeUnit: string;
  @Input() format: string;
  @Output() onStart: EventEmitter<DurationTimerComponent> = new EventEmitter<DurationTimerComponent>();
  @Output() onStop: EventEmitter<DurationTimerComponent> = new EventEmitter<DurationTimerComponent>();
  @Output() onTick: EventEmitter<TimeInterface> = new EventEmitter<TimeInterface>();
  @Output() onComplete: EventEmitter<DurationTimerComponent> = new EventEmitter<DurationTimerComponent>();

  constructor(private elt: ElementRef, private renderer: Renderer2) {
    // Initialization
    this.autoStart = true;
    this.timeoutId = null;

    this.format = 'default';
  }

  ngAfterViewInit() {
    const ngContentNode = this.elt.nativeElement.lastChild;    // Get last child, defined by user or span
    this.ngContentSchema = ngContentNode ? ngContentNode.nodeValue : '';
    this.initVar();
    this.resetTimeout();

    if (this.autoStart === undefined || this.autoStart === true) {
      this.start();
    } else {
      setTimeout(()=>{
        this.computeTimeUnits();
      });
    }
  }

  ngOnDestroy() {
    this.resetTimeout();
  }

  @HostBinding('style.min-width.rem')
  elMinWidth: number = 4;

  /**
   * Start the timer
   */
  public start(date: Date = null) {
    if (date) {
      this.startDate = date;
    }
    this.initVar();
    this.resetTimeout();
    this.startTickCount();

    this.onStart.emit(this);
  }

  /**
   * Resume the timer
   */
  public resume() {
    this.resetTimeout();

    this.startTickCount();
  }

  /**
   * Stop the timer
   */
  public stop() {
    this.clear();

    this.onStop.emit(this);
  }

  /**
   * Reset the timer
   */
  public reset() {
    this.initVar();
    this.resetTimeout();
    this.clear();
    this.computeTimeUnits();
    this.renderText();
  }

  /**
   * Get the time information
   * @returns TimeInterface
   */
  public get() {
    return {
      seconds: this.seconds,
      minutes: this.minutes,
      hours: this.hours,
      days: this.days,
      timer: this.timeoutId
    };
  }

  /**
   * Initialize variable before start
   */
  private initVar() {
    this.startMoment = DateTime.fromJSDate(this.startDate);//.minus({second: this.duration?.inSeconds ?? 0});

    // Determine auto format
    if (!this.format) {
      this.format = (this.ngContentSchema.length > 5) ? 'user' : 'default';
    }
    this.renderText();
  }

  /**
   * Reset timeout
   */
  private resetTimeout() {
    if (this.timeoutId) {
      clearInterval(this.timeoutId);
    }
  }

  /**
   * Render the time to DOM
   */
  private renderText() {
    let outputText;
    if (this.format === 'user') {
      // User presentation
      const items = {
        'seconds': this.seconds,
        'minutes': this.minutes,
        'hours': this.hours,
        'days': this.days
      };

      outputText = this.ngContentSchema;

      for (const key of Object.keys(items)) {
        outputText = outputText.replace('[' + key + ']', (items as any)[key].toString());
      }
    } else if (this.format === 'intelli') {
      // Intelli presentation
      outputText = '';
      if (this.days > 0) {
        outputText += this.days.toString() + 'day' + ((this.days > 1) ? 's' : '') + ' ';
      }
      if ((this.hours > 0) || (this.days > 0)) {
        outputText += this.hours.toString() + 'h ';
      }
      if (((this.minutes > 0) || (this.hours > 0)) && (this.days === 0)) {
        outputText += this.minutes.toString().padStart(2, '0') + 'min ';
      }
      if ((this.hours === 0) && (this.days === 0)) {
        outputText += this.seconds.toString().padStart(2, '0') + 's';
      }
    } else if (this.format === 'hms') {
      // Hms presentation
      outputText = '';
      this.elt.nativeElement.styles
      if (this.days > 0) {
        outputText += this.days.toString() + '.';
        this.elMinWidth = 5.3;
        if(this.days > 9){
          this.elMinWidth = 5.7;
        }
      }
      outputText += this.hours.toString().padStart(2, '0') + ':';
      outputText += this.minutes.toString().padStart(2, '0') + ':';
      outputText += this.seconds.toString().padStart(2, '0');
    } else if (this.format === 'ms') {
      // ms presentation
      outputText = '';
      if (this.hours > 0) {
        outputText = this.hours.toString().padStart(2, '0') + ':';
      }
      outputText += this.minutes.toString().padStart(2, '0') + ':';
      outputText += this.seconds.toString().padStart(2, '0');
    } else {
      // Default presentation
      outputText = this.days.toString() + 'd ';
      outputText += this.hours.toString() + 'h ';
      outputText += this.minutes.toString() + 'm ';
      outputText += this.seconds.toString() + 's';
    }

    this.renderer.setProperty(this.elt.nativeElement, 'innerHTML', outputText);
  }

  private clear() {
    this.resetTimeout();
    this.timeoutId = null;
  }

  /**
   * Compute each unit (seconds, minutes, hours, days) for further manipulation
   * @protected
   */
  protected computeTimeUnits(now: DateTime = null) {
    if (now == null) {
      now = DateTime.fromJSDate(this.startDate);
    }

    let duration1;
    if (this.startMoment.isValid) {
      duration1 = this.startMoment.minus({second: this.duration?.inSeconds ?? 0}).diff(now, ["days", "hours", 'minutes', 'seconds']);
    } else {
      let dateTime = DateTime.now();
      let dateTime1 = dateTime.minus({second: this.duration?.inSeconds ?? 0});
      duration1 = dateTime1.diff(dateTime, ["days", "hours", 'minutes', 'seconds']);

    }
    this.seconds = Math.abs(Math.floor(duration1.seconds));
    this.minutes = Math.abs(duration1.minutes);
    this.hours = Math.abs(duration1.hours);
    this.days = Math.abs(duration1.days);

    this.renderText();
  }

  /**
   * Start tick count, base of this component
   * @protected
   */
  protected startTickCount() {
    const that = this;

    that.timeoutId = setInterval(function () {

      let now = DateTime.now();
      that.computeTimeUnits(now);

      const timer: TimeInterface = {
        seconds: that.seconds,
        minutes: that.minutes,
        hours: that.hours,
        days: that.days,
        timer: that.timeoutId
      };

      that.onTick.emit(timer);
    }, 1000); // Each seconds
  }

}
